import { DirtyFlag } from './types';

/**
 * Tracks which parts of the rendering state have changed
 *
 * Pattern: Dirty Flag
 * Purpose: Avoid unnecessary recalculations and redraws
 */

export class DirtyTracker {
  private flags = new Set<DirtyFlag>();
  private listeners = new Map<DirtyFlag, Set<() => void>>();

  /**
   * Mark a specific flag as dirty
   */
  markDirty(flag: DirtyFlag): void {
    const wasClean = !this.flags.has(flag);
    this.flags.add(flag);

    if (wasClean) {
      this.notifyListeners(flag);
    }
  }

  /**
   * Mark multiple flags as dirty
   */
  markMultipleDirty(flags: DirtyFlag[]): void {
    flags.forEach(flag => this.markDirty(flag));
  }

  /**
   * Check if a specific flag is dirty
   */
  isDirty(flag: DirtyFlag): boolean {
    return this.flags.has(flag);
  }

  /**
   * Check if any of the provided flags are dirty
   */
  isAnyDirty(flags: DirtyFlag[]): boolean {
    return flags.some(flag => this.flags.has(flag));
  }

  /**
   * Check if all provided flags are dirty
   */
  areAllDirty(flags: DirtyFlag[]): boolean {
    return flags.every(flag => this.flags.has(flag));
  }

  /**
   * Clear a specific dirty flag
   */
  clearDirty(flag: DirtyFlag): void {
    this.flags.delete(flag);
  }

  /**
   * Clear multiple dirty flags
   */
  clearMultiple(flags: DirtyFlag[]): void {
    flags.forEach(flag => this.flags.delete(flag));
  }

  /**
   * Clear all dirty flags
   */
  clearAll(): void {
    this.flags.clear();
  }

  /**
   * Get all currently dirty flags
   */
  getDirtyFlags(): DirtyFlag[] {
    return Array.from(this.flags);
  }

  /**
   * Check if anything is dirty
   */
  hasAnyDirty(): boolean {
    return this.flags.size > 0;
  }

  /**
   * Subscribe to dirty flag changes
   */
  subscribe(flag: DirtyFlag, callback: () => void): () => void {
    if (!this.listeners.has(flag)) {
      this.listeners.set(flag, new Set());
    }
    this.listeners.get(flag)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(flag)?.delete(callback);
    };
  }

  /**
   * Notify listeners when a flag becomes dirty
   */
  private notifyListeners(flag: DirtyFlag): void {
    const callbacks = this.listeners.get(flag);
    if (callbacks) {
      callbacks.forEach(callback => callback());
    }
  }

  /**
   * Get statistics about dirty state
   */
  getStats(): { dirtyCount: number; flags: DirtyFlag[] } {
    return {
      dirtyCount: this.flags.size,
      flags: this.getDirtyFlags(),
    };
  }
}
